import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import {
    deleteBlogPost,
    fetchBlogComments,
    fetchBlogPost,
    submitBlogReview,
} from "../../utils/apiUtils";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { AvatarMark, ImageLightbox, Stars, displayName, formatRelative, useBlogMediaSrc } from "./blogShared.jsx";
import { extractS3Keys } from "./blogMedia.js";
import BlogHtmlBody from "./BlogHtmlBody.jsx";
import "./Blog.scss";

const COMMENT_PAGE = 6;

const flagLabel = {
    phi: "protected health information",
    pii: "personal information",
    foul: "language we cannot publish",
};

const moderationMessage = (err) => {
    const flags = err.cause?.flags;
    if (flags?.length) {
        return `Cannot publish: ${flags.map((f) => flagLabel[f] || f).join(", ")}.`;
    }
    return err.message || "Something went wrong";
};

const BlogImage = ({ imageKey, onOpen }) => {
    const src = useBlogMediaSrc(imageKey, "image");
    if (!imageKey || !src) return null;
    return (
        <button
            type="button"
            className="blog__image-btn"
            onClick={() => onOpen?.(src)}
            aria-label="View image full screen"
        >
            <img className="blog__image" src={src} alt="" />
        </button>
    );
};

const postImageKeys = (data) => (
    data?.imageKeys?.length ? data.imageKeys : data?.imageKey ? [data.imageKey] : []
);

const BlogImages = ({ imageKeys }) => {
    const keys = imageKeys || [];
    const [index, setIndex] = useState(0);
    const [openSrc, setOpenSrc] = useState("");
    const many = keys.length > 1;

    useEffect(() => {
        setIndex(0);
    }, [keys.join("|")]);

    if (!keys.length) return null;

    const go = (dir) => {
        setIndex((current) => (current + dir + keys.length) % keys.length);
    };

    return (
        <div
            className={`blog__carousel${many ? " is-many" : ""}`}
            tabIndex={many ? 0 : undefined}
            onKeyDown={(event) => {
                if (!many) return;
                if (event.key === "ArrowLeft") {
                    event.preventDefault();
                    go(-1);
                }
                if (event.key === "ArrowRight") {
                    event.preventDefault();
                    go(1);
                }
            }}
        >
            <div className="blog__carousel-frame">
                <div
                    className="blog__carousel-track"
                    style={many ? { transform: `translateX(-${index * 100}%)` } : undefined}
                >
                    {keys.map((key) => (
                        <div key={key} className="blog__carousel-slide">
                            <BlogImage imageKey={key} onOpen={setOpenSrc} />
                        </div>
                    ))}
                </div>
                {many ? (
                    <>
                        <button
                            type="button"
                            className="blog__carousel-nav blog__carousel-nav--prev"
                            onClick={() => go(-1)}
                            aria-label="Previous image"
                        >
                            <LuChevronLeft size="1.25rem" />
                        </button>
                        <button
                            type="button"
                            className="blog__carousel-nav blog__carousel-nav--next"
                            onClick={() => go(1)}
                            aria-label="Next image"
                        >
                            <LuChevronRight size="1.25rem" />
                        </button>
                    </>
                ) : null}
            </div>
            {many ? (
                <div className="blog__carousel-bar">
                    <div className="blog__carousel-dots">
                        {keys.map((key, i) => (
                            <button
                                key={key}
                                type="button"
                                className={i === index ? "is-on" : ""}
                                aria-label={`Image ${i + 1} of ${keys.length}`}
                                aria-current={i === index}
                                onClick={() => setIndex(i)}
                            />
                        ))}
                    </div>
                    <em>{index + 1} / {keys.length}</em>
                </div>
            ) : null}
            {openSrc ? <ImageLightbox src={openSrc} onClose={() => setOpenSrc("")} /> : null}
        </div>
    );
};

const BlogVideo = ({ videoKey }) => {
    const src = useBlogMediaSrc(videoKey, "video");
    if (!videoKey || !src) return null;
    return <video className="blog__video" src={src} controls playsInline preload="metadata" />;
};

const Note = ({ row }) => (
    <li className="blog__note">
        <AvatarMark email={row.authorEmail} />
        <div className="blog__note-main">
            <p className="blog__note-meta">
                <strong className="blog__name">{displayName(row.authorEmail)}</strong>
                <span aria-hidden="true">·</span>
                <em>{formatRelative(row.createdAt)}</em>
            </p>
            <p className="blog__note-body">{row.body}</p>
        </div>
    </li>
);

const BlogPost = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [comment, setComment] = useState("");
    const [picked, setPicked] = useState(0);
    const [formError, setFormError] = useState("");
    const [scrollRoot, setScrollRoot] = useState(null);
    const { ref, inView } = useInView({
        root: scrollRoot,
        rootMargin: "160px",
        threshold: 0,
    });

    const { data, error, isPending } = useQuery({
        queryKey: ["blog-post", id],
        queryFn: () => fetchBlogPost(id),
        enabled: Boolean(id),
    });

    const comments = useInfiniteQuery({
        queryKey: ["blog-comments", id],
        queryFn: async ({ pageParam }) => fetchBlogComments(id, { cursor: pageParam, limit: COMMENT_PAGE }),
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage?.nextCursor || undefined,
        enabled: Boolean(id),
    });

    const fetchMoreRef = useRef(comments.fetchNextPage);
    const canFetchRef = useRef(false);
    fetchMoreRef.current = comments.fetchNextPage;
    canFetchRef.current = Boolean(comments.hasNextPage) && !comments.isFetchingNextPage;

    useEffect(() => {
        if (inView && canFetchRef.current) {
            fetchMoreRef.current();
        }
    }, [inView]);

    useEffect(() => {
        if (!scrollRoot) return undefined;
        const onScroll = () => {
            const remain = scrollRoot.scrollHeight - scrollRoot.scrollTop - scrollRoot.clientHeight;
            if (remain < 180 && canFetchRef.current) {
                fetchMoreRef.current();
            }
        };
        scrollRoot.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => scrollRoot.removeEventListener("scroll", onScroll);
    }, [scrollRoot]);

    const syncStoredAverage = (summary) => {
        const next = {
            ratingSum: summary.ratingSum,
            ratingCount: summary.ratingCount,
            ratingAvg: summary.ratingAvg,
            myRating: summary.myRating,
        };
        queryClient.setQueryData(["blog-post", id], (old) => (old ? { ...old, ...next } : old));
        const patchRows = (old) => {
            if (!old) return old;
            if (old.pages) {
                return {
                    ...old,
                    pages: old.pages.map((page) => ({
                        ...page,
                        rows: (page.rows || []).map((row) => (
                            row.id === id ? { ...row, ...next } : row
                        )),
                    })),
                };
            }
            if (old.rows) {
                return {
                    ...old,
                    rows: old.rows.map((row) => (row.id === id ? { ...row, ...next } : row)),
                };
            }
            return old;
        };
        queryClient.setQueriesData({ queryKey: ["blog-feed"] }, patchRows);
        queryClient.setQueriesData({ queryKey: ["blog-search"] }, patchRows);
    };

    const reviewMut = useMutation({
        mutationFn: ({ stars, body }) => submitBlogReview(id, { stars, body }),
        onSuccess: (result) => {
            setComment("");
            setFormError("");
            if (result.myRating) setPicked(result.myRating);
            syncStoredAverage(result);
            if (result.comment) {
                queryClient.setQueryData(["blog-comments", id], (old) => {
                    if (!old?.pages?.length) {
                        return { pages: [{ rows: [result.comment], nextCursor: null }], pageParams: [undefined] };
                    }
                    const [first, ...rest] = old.pages;
                    return {
                        ...old,
                        pages: [{ ...first, rows: [...(first.rows || []), result.comment] }, ...rest],
                    };
                });
                queryClient.setQueryData(["blog-post", id], (old) => (
                    old ? { ...old, commentCount: (old.commentCount || 0) + 1 } : old
                ));
            }
        },
        onError: (err) => setFormError(moderationMessage(err)),
    });

    const deleteMut = useMutation({
        mutationFn: () => deleteBlogPost(id),
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: ["blog-post", id] });
            queryClient.invalidateQueries({ queryKey: ["blog-feed"] });
            queryClient.invalidateQueries({ queryKey: ["blog-search"] });
            queryClient.invalidateQueries({ queryKey: ["blog-authors"] });
            navigate("/blog");
        },
        onError: (err) => setFormError(err.message || "Could not delete"),
    });

    useEffect(() => {
        setFormError("");
        setComment("");
        setPicked(0);
    }, [id]);

    useEffect(() => {
        if (data?.myRating) setPicked(data.myRating);
    }, [data?.myRating]);

    const commentRows = (comments.data?.pages || []).flatMap((page) => page.rows || []);
    const commentTotal = data?.commentCount ?? commentRows.length;

    if (isPending) {
        return (
            <div className="container blog blog--page blog--post scrollable">
                <div className="blog__sheet">
                    <p className="blog__status">Loading…</p>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="container blog blog--page blog--post scrollable">
                <div className="blog__sheet">
                    <p className="blog__error">{error?.message || "Post not found"}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container blog blog--page blog--post scrollable" ref={setScrollRoot}>
            <div className="blog__sheet">
                <article className="blog__article">
                    <div className="blog__article-head">
                        <div className="blog__who">
                            <AvatarMark email={data.authorEmail} />
                            <div>
                                <strong className="blog__name">{displayName(data.authorEmail)}</strong>
                                <em>{formatRelative(data.createdAt)}</em>
                            </div>
                        </div>
                        <div className="blog__article-rating">
                            <Stars value={data.ratingAvg} readOnly />
                            <em>
                                {data.ratingCount
                                    ? `${Number(data.ratingAvg).toFixed(1)} · ${data.ratingCount} rating${data.ratingCount === 1 ? "" : "s"}`
                                    : "No ratings yet"}
                            </em>
                        </div>
                    </div>
                    <h1>{data.title}</h1>
                    <BlogImages imageKeys={postImageKeys(data).filter((key) => !extractS3Keys(data.body).includes(key))} />
                    <BlogVideo videoKey={data.videoKey} />
                    <div className="blog__body">
                        <BlogHtmlBody html={data.body} />
                    </div>
                </article>

                <section className="blog__section blog__section--compose">
                    <div className="blog__section-head">
                        <h2>Add a comment</h2>
                        {data.mine ? (
                            <div className="blog__owner">
                                <Link to={`/blog/${id}/edit`} className="blog__btn blog__btn--ghost">
                                    Edit
                                </Link>
                                <button
                                    type="button"
                                    className="blog__btn blog__btn--danger"
                                    disabled={deleteMut.isPending}
                                    onClick={() => {
                                        if (!window.confirm("Delete this post? This cannot be undone.")) return;
                                        deleteMut.mutate();
                                    }}
                                >
                                    {deleteMut.isPending ? "Deleting…" : "Delete"}
                                </button>
                            </div>
                        ) : null}
                    </div>
                    <form
                        className="blog__form blog__form--comment"
                        onSubmit={(event) => {
                            event.preventDefault();
                            const body = comment.trim();
                            if (!picked && !body) {
                                setFormError("Add a rating or a comment.");
                                return;
                            }
                            reviewMut.mutate({
                                stars: picked || undefined,
                                body,
                            });
                        }}
                    >
                        <div className="blog__field">
                            <span>Your rating</span>
                            <Stars
                                value={picked}
                                disabled={reviewMut.isPending}
                                onPick={setPicked}
                            />
                        </div>
                        <textarea
                            placeholder="Write a comment"
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                        />
                        <button type="submit" className="blog__btn blog__btn--primary" disabled={reviewMut.isPending}>
                            Submit
                        </button>
                    </form>
                    {formError ? <p className="blog__error">{formError}</p> : null}
                </section>

                <section className="blog__section">
                    <h2>
                        {commentTotal
                            ? `Comments · ${commentTotal}`
                            : "Comments"}
                    </h2>
                    {comments.isPending ? <p className="blog__status">Loading comments…</p> : null}
                    {!comments.isPending && !commentRows.length ? (
                        <p className="blog__empty">No comments yet.</p>
                    ) : null}
                    <ul className="blog__items">
                        {commentRows.map((row) => (
                            <Note key={row.id} row={row} />
                        ))}
                    </ul>
                    <div
                        ref={ref}
                        className="blog__sentinel"
                        data-has-next={comments.hasNextPage ? "1" : "0"}
                    />
                    {comments.isFetchingNextPage ? <p className="blog__status">Loading more…</p> : null}
                    {comments.hasNextPage && !comments.isFetchingNextPage ? (
                        <button
                            type="button"
                            className="blog__more-link"
                            onClick={() => comments.fetchNextPage()}
                        >
                            Load more comments
                        </button>
                    ) : null}
                </section>
            </div>
        </div>
    );
};

export default BlogPost;
